import revisionStates from '@/features/ClusterConfigs/utils/revisionStates'
import type { LatestClusterRevision } from '@/generated/http-clients/v3ApiSchemas'
import { Box, Icon, Stack, Text, Tooltip } from '@chakra-ui/react'
import {
  Ban,
  Circle,
  CircleAlert,
  CircleSlash,
  CircleX,
  FileCheck,
  FilePlus,
  FileQuestion,
  FileX,
  LoaderCircle,
  Lock,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import type { PropsWithChildren } from 'react'
import styles from './clusterConfigState.module.css'

/**
 * Fields needed in revision:
 * - revision (of type ClusterRevision)
 *   - approval_state.state
 *   - rollout.state
 *   - rollout.state_message
 *   - hive_state.state
 * - disabled
 */
type ClusterConfigStateProps = {
  revision: LatestClusterRevision
}

type Props = {
  hoverText: string
} & ClusterConfigStateProps['revision']

function HoverText({ revision, hoverText }: Readonly<Props>) {
  return (
    <Stack gap={0}>
      <Text color="white" fontWeight="bold">
        {hoverText}
      </Text>
      <Text color="gray" fontSize="xs">
        Approval state: {revision.approval_state.state}
      </Text>
      <Text color="gray" fontSize="xs">
        Rollout state: {revision.rollout?.state ?? 'undefined'}
      </Text>
      <Text color="gray" fontSize="xs">
        Hive state: {revision.hive_state?.state ?? 'undefined'}
      </Text>
      {revision.rollout?.state_message ? (
        <Text color="gray" fontSize="xs">
          State message: {revision.rollout?.state_message}
        </Text>
      ) : null}
    </Stack>
  )
}

export function ClusterConfigCombinedStates({
  /**
   * The revision from which we need to interpret states.
   */
  revision,
}: PropsWithChildren<ClusterConfigStateProps>) {
  function renderContent() {
    if (revisionStates.isDeleting(revision)) {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Icon as={Trash2} size="md" color="blue.500" />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText {...revision} hoverText="Deleting..." />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    if (revisionStates.isDisabled(revision)) {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Icon as={CircleSlash} size="md" color="gray.500" />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText {...revision} hoverText="Disabled" />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    if (revisionStates.hasAProblem(revision)) {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Icon as={CircleX} size="md" color="red.500" />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText {...revision} hoverText="Has a problem" />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    if (revisionStates.isBlocked(revision)) {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Icon as={Ban} size="md" color="red.500" />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText {...revision} hoverText="Blocked" />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    if (revisionStates.isLocked(revision)) {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Icon as={Lock} size="md" color="gray.500" />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText {...revision} hoverText="Locked" />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    if (revisionStates.isRunning(revision)) {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Icon as={Circle} size="md" color="transparent" fill="green.600" />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText {...revision} hoverText="Running" />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    if (revisionStates.isSubmitted(revision)) {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Icon as={FilePlus} size="md" color="gray.500" />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText {...revision} hoverText="Submitted" />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    if (revisionStates.isApproved(revision)) {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Icon as={FileCheck} size="md" color="green.500" />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText {...revision} hoverText="Approved" />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    if (revisionStates.isRejected(revision)) {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Icon as={FileX} size="md" color="red.500" />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText {...revision} hoverText="Rejected" />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    if (revisionStates.isUnknown(revision)) {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Icon as={FileQuestion} size="md" color="orange.500" />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText {...revision} hoverText="Unknown" />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    if (revisionStates.isUpdating(revision)) {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Icon
              as={LoaderCircle}
              size="md"
              color="blue.500"
              className={styles.rotatingCw}
            />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText {...revision} hoverText="Updating" />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    if (revisionStates.isRestarting(revision)) {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Icon
              as={RefreshCw}
              size="md"
              color="blue.500"
              className={styles.rotatingCw}
            />
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>
              <Tooltip.Arrow />
              <HoverText
                {...revision}
                hoverText="Broker restarting or starting for the first time"
              />
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      )
    }

    // By default, display a warning
    return (
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <Icon as={CircleAlert} size="md" color="orange.500" />
        </Tooltip.Trigger>
        <Tooltip.Positioner>
          <Tooltip.Content>
            <Tooltip.Arrow />
            <HoverText {...revision} hoverText="Unknown" />
          </Tooltip.Content>
        </Tooltip.Positioner>
      </Tooltip.Root>
    )
  }

  return <Box paddingY="0.25rem">{renderContent()}</Box>
}
